import React, { useState } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { Clock } from 'lucide-react';
import StudentAssignment from '@/components/StudentAssignment';
import MultiFilePicker from '@/components/shared/MultiFilePicker';
import { uploadFile } from '@/lib/uploadFile';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_FILES = 10;

export default function ContentCreateDialog({
  open, onClose, classId, contentType, uploadLabel,
  classStudents, isStudent, token, t, onCreated
}) {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [scheduleEnabled, setScheduleEnabled] = useState(false);
  const [scheduledDate, setScheduledDate] = useState('');
  const [scheduledTime, setScheduledTime] = useState('');
  const [selectedStudents, setSelectedStudents] = useState([]);

  const reset = () => {
    setTitle(''); setDescription(''); setFiles([]);
    setScheduleEnabled(false); setScheduledDate(''); setScheduledTime('');
    setSelectedStudents([]);
  };

  const handleCreate = async () => {
    if (!title.trim()) {
      toast.error('El título es obligatorio');
      return;
    }
    setUploading(true);
    try {
      const uploadedFiles = [];
      for (const f of files) uploadedFiles.push(await uploadFile(f, token));
      const scheduled_for = (scheduleEnabled && scheduledDate && scheduledTime)
        ? new Date(`${scheduledDate}T${scheduledTime}`).toISOString() : null;
      const assigned_to = selectedStudents.length > 0 ? selectedStudents : null;

      await axios.post(`${API}/content/create`, {
        class_id: classId, content_type: contentType,
        title, description, files: uploadedFiles, links: [],
        scheduled_for, assigned_to
      }, { headers: { Authorization: `Bearer ${token}` } });

      toast.success(scheduleEnabled ? t('content_scheduled') : t('success'));
      reset();
      onCreated();
      onClose();
    } catch (err) {
      toast.error(err?.message || err?.response?.data?.detail || t('error'));
    } finally { setUploading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle style={{ fontFamily: 'Montserrat' }}>{uploadLabel}</DialogTitle></DialogHeader>
        <div className="space-y-4 pt-2">
          <div><Label>{t('title')}</Label><Input value={title} onChange={e => setTitle(e.target.value)} data-testid={`${contentType}-title-input`} /></div>
          <div><Label>{t('description')}</Label><Textarea value={description} onChange={e => setDescription(e.target.value)} data-testid={`${contentType}-desc-input`} /></div>

          <div>
            <Label className="mb-2 block">{t('attach_file')}</Label>
            <MultiFilePicker
              files={files}
              setFiles={setFiles}
              maxFiles={MAX_FILES}
              testIdPrefix={`${contentType}-files`}
              firstButtonLabel="Adjuntar archivo"
              buttonLabel="Adjuntar otro archivo"
            />
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
            <div className="flex items-center justify-between mb-3">
              <Label className="text-sm font-bold text-slate-700 flex items-center gap-1.5">
                <Clock className="w-4 h-4 text-blue-500" /> {t('schedule_publication')}
              </Label>
              <div className="flex items-center gap-2">
                <button type="button" onClick={() => setScheduleEnabled(false)}
                  className={`px-3 py-1 text-xs rounded-full font-medium transition-colors ${!scheduleEnabled ? 'bg-blue-600 text-white' : 'bg-slate-200 text-slate-600 hover:bg-slate-300'}`}
                  data-testid={`${contentType}-publish-now-btn`}>{t('publish_now')}</button>
                <button type="button" onClick={() => setScheduleEnabled(true)}
                  className={`px-3 py-1 text-xs rounded-full font-medium transition-colors ${scheduleEnabled ? 'bg-blue-600 text-white' : 'bg-slate-200 text-slate-600 hover:bg-slate-300'}`}
                  data-testid={`${contentType}-schedule-later-btn`}>{t('publish_later')}</button>
              </div>
            </div>
            {scheduleEnabled && (
              <div className="flex gap-3 mt-2">
                <div className="flex-1">
                  <Label className="text-xs text-slate-500 mb-1 block">Fecha</Label>
                  <Input type="date" value={scheduledDate} onChange={e => setScheduledDate(e.target.value)}
                    className="bg-white" data-testid={`${contentType}-schedule-date-input`} min={new Date().toISOString().split('T')[0]} />
                </div>
                <div className="flex-1">
                  <Label className="text-xs text-slate-500 mb-1 block">Hora</Label>
                  <Input type="time" value={scheduledTime} onChange={e => setScheduledTime(e.target.value)}
                    className="bg-white" data-testid={`${contentType}-schedule-time-input`} />
                </div>
              </div>
            )}
          </div>

          {!isStudent && classStudents.length > 0 && (
            <StudentAssignment students={classStudents}
              selectedStudents={selectedStudents} setSelectedStudents={setSelectedStudents}
              label="Asignar a Estudiantes" />
          )}

          <Button onClick={handleCreate} disabled={uploading} className="w-full" data-testid={`create-${contentType}-submit`}>
            {uploading ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : t('create')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
